import Phaser from "phaser";
import type { GameTheme } from "../themes/types";
import { darkenColor, lightenColor } from "../utils/colors";
import { drawRoundedRect, drawGlossyOverlay } from "../utils/graphics";
import { animateButtonPress } from "../utils/animations";

export interface ButtonConfig {
  x: number;
  y: number;
  width?: number;
  height?: number;
  text: string;
  fontSize?: number;
  theme: GameTheme;
  variant?: "primary" | "secondary" | "success" | "warning" | "danger";
  onClick?: () => void;
}

export class Button extends Phaser.GameObjects.Container {
  private background!: Phaser.GameObjects.Graphics;
  private label!: Phaser.GameObjects.Text;
  private config: Required<ButtonConfig>;
  private isPressed = false;
  private isEnabled = true;

  constructor(scene: Phaser.Scene, config: ButtonConfig) {
    super(scene, config.x, config.y);

    // Set defaults
    this.config = {
      ...config,
      width: config.width ?? 200,
      height: config.height ?? 60,
      fontSize: config.fontSize ?? 24,
      variant: config.variant ?? "primary",
      onClick: config.onClick ?? (() => {}),
    };

    this.createButton();
    this.setupInteractivity();

    scene.add.existing(this);
  }

  private createButton(): void {
    const { width, height, text, fontSize } = this.config;

    // Get colors based on variant
    const backgroundColor = this.getBackgroundColor();
    const borderColor = darkenColor(backgroundColor, 20);

    // Create background graphics
    this.background = this.scene.add.graphics();
    this.add(this.background);

    // Draw button
    const borderRadius = 16;
    const borderWidth = 4;

    drawRoundedRect(
      this.background,
      -width / 2,
      -height / 2,
      width,
      height,
      borderRadius,
      backgroundColor,
      borderColor,
      borderWidth
    );

    // Add glossy overlay
    drawGlossyOverlay(
      this.background,
      -width / 2 + borderWidth,
      -height / 2 + borderWidth,
      width - borderWidth * 2,
      height - borderWidth * 2,
      borderRadius - borderWidth
    );

    // Create text label
    this.label = this.scene.add.text(0, 0, text, {
      fontFamily: "Arial",
      fontSize: `${fontSize}px`,
      color: "#ffffff",
      stroke: "#000000",
      strokeThickness: 4,
      fontStyle: "bold",
    });
    this.label.setOrigin(0.5);
    this.add(this.label);

    // Add drop shadow
    this.setDepth(1);
  }

  private getBackgroundColor(): number {
    const { theme, variant } = this.config;

    switch (variant) {
      case "primary":
        return theme.primary;
      case "secondary":
        return theme.secondary;
      case "success":
        return theme.success;
      case "warning":
        return theme.warning;
      case "danger":
        return theme.danger;
      default:
        return theme.primary;
    }
  }

  private setupInteractivity(): void {
    const { width, height } = this.config;

    // Create interactive zone
    const hitArea = new Phaser.Geom.Rectangle(
      -width / 2,
      -height / 2,
      width,
      height
    );

    this.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    // Pointer over
    this.on("pointerover", () => {
      if (!this.isEnabled) return;
      this.adjustBrightness(10);
      this.scene.input.setDefaultCursor("pointer");
    });

    // Pointer out
    this.on("pointerout", () => {
      if (!this.isEnabled) return;
      this.adjustBrightness(0);
      this.scene.input.setDefaultCursor("default");
    });

    // Pointer down
    this.on("pointerdown", () => {
      if (!this.isEnabled) return;
      this.isPressed = true;
      animateButtonPress(this.scene, this);
    });

    // Pointer up
    this.on("pointerup", () => {
      if (!this.isEnabled || !this.isPressed) return;
      this.isPressed = false;
      this.config.onClick();
    });
  }

  private adjustBrightness(percent: number): void {
    const baseColor = this.getBackgroundColor();
    const adjustedColor =
      percent > 0 ? lightenColor(baseColor, percent) : baseColor;
    const borderColor = darkenColor(adjustedColor, 20);

    // Redraw button with new colors
    const { width, height } = this.config;
    this.background.clear();

    drawRoundedRect(
      this.background,
      -width / 2,
      -height / 2,
      width,
      height,
      16,
      adjustedColor,
      borderColor,
      4
    );

    drawGlossyOverlay(
      this.background,
      -width / 2 + 4,
      -height / 2 + 4,
      width - 8,
      height - 8,
      12
    );
  }

  public setText(text: string): void {
    this.label.setText(text);
  }

  public setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    this.alpha = enabled ? 1 : 0.5;
    this.setInteractive(enabled);
  }

  public destroy(): void {
    this.background.destroy();
    this.label.destroy();
    super.destroy();
  }
}
